import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, CalendarCheck, MessagesSquare, ShieldCheck, Copy, Check } from "lucide-react";
import { api, PHONE_DISPLAY, waDefault, waLink } from "@/lib/api";
import { FadeIn, MaskLine, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import PropertyCard from "@/components/PropertyCard";
import ContactForm from "@/components/ContactForm";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const HERO_IMG =
  "https://images.pexels.com/photos/36676879/pexels-photo-36676879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABOUT_IMG =
  "https://images.pexels.com/photos/29334668/pexels-photo-29334668.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
  {
    n: "01",
    title: "Quiénes somos",
    body: "Inmobiliaria Tecnohogar nace con una idea sencilla: tratar cada casa como si fuera la nuestra. Somos una inmobiliaria de proximidad que acompaña a las familias en uno de los momentos más importantes de su vida, con trato directo y sin intermediarios.",
  },
  {
    n: "02",
    title: "Nuestro compromiso",
    body: "Tu hogar, nuestro compromiso no es un eslogan: es nuestra forma de trabajar. Escuchamos lo que necesitas, te enseñamos solo las propiedades que encajan contigo y te acompañamos antes, durante y después de la firma.",
  },
  {
    n: "03",
    title: "Excelencia jurídica",
    body: "Cada operación se revisa con rigor: documentación al día, estudio de cargas y contratos claros. Queremos que firmes con total tranquilidad y seguridad jurídica.",
  },
  {
    n: "04",
    title: "Servicio cercano",
    body: "Nada de formularios interminables ni esperas: nos escribes por WhatsApp y te respondemos al momento. Visitas concertadas a tu medida, también fuera de horario.",
  },
];

const HERO = {
  eyebrow: "Inmobiliaria · Compra, venta y alquiler",
  sub: "Propiedades seleccionadas con criterio, atención directa por WhatsApp y acompañamiento completo en cada firma.",
};

const Home = () => {
  const [featured, setFeatured] = useState(null);
  const [copied, setCopied] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api
      .get("/properties", { params: { featured: true } })
      .then((r) => {
        if (r.data.length) setFeatured(r.data);
        else return api.get("/properties").then((r2) => setFeatured(r2.data.slice(0, 6)));
      })
      .catch(() => setFeatured([]));
  }, []);

  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/#contacto`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="kenburns h-full w-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/40" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-32 md:px-8">
          <FadeIn delay={0.05} y={12}>
            <Eyebrow className="justify-center md:justify-start">{HERO.eyebrow}</Eyebrow>
          </FadeIn>
          <h1 className="mt-6 font-serif text-5xl leading-[1.02] md:text-7xl lg:text-8xl">
            <MaskLine delay={0.15}>
              <em className="font-light not-italic text-ivory">Tu hogar,</em>
            </MaskLine>
            <MaskLine delay={0.35}>
              <span className="text-gold-metallic italic">nuestro compromiso</span>
            </MaskLine>
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-ivory/80 md:text-lg">{HERO.sub}</p>
          </FadeIn>
          <FadeIn delay={0.85}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/propiedades"
                data-testid="hero-cta-properties"
                className="btn-gold-metallic inline-flex items-center justify-center rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-ink"
              >
                Ver propiedades
              </Link>
              <a
                href={waDefault()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="inline-flex items-center justify-center gap-3 rounded-full border border-wa/40 bg-wa/10 px-8 py-4 text-sm font-bold uppercase tracking-[0.18em] text-wa backdrop-blur transition-all hover:bg-wa hover:text-ink"
              >
                <WhatsAppIcon className="h-5 w-5" /> WhatsApp {PHONE_DISPLAY}
              </a>
            </div>
          </FadeIn>
        </div>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2.2 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-gold/70"
        >
          <ChevronDown size={26} />
        </motion.div>
      </section>

      <Marquee />

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-8 md:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Selección Tecnohogar</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
              Propiedades <span className="text-gold-metallic">destacadas</span>
            </h2>
          </div>
          <Link
            to="/propiedades"
            data-testid="home-view-all-properties"
            className="group flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-gold hover:text-gold-light"
          >
            Ver todas
            <span className="h-px w-8 bg-gold transition-all group-hover:w-12" />
          </Link>
        </div>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {featured === null && (
            [1, 2, 3].map((i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl border border-gold/10 bg-ink2" data-testid="featured-skeleton" />
            ))
          )}
          {featured && featured.length === 0 && (
            <div className="col-span-full rounded-2xl border border-gold/15 bg-ink2/70 p-10 text-center" data-testid="featured-empty">
              <p className="font-serif text-2xl text-ivory">Pronto publicaremos nuevas propiedades.</p>
              <p className="mt-3 text-sm text-stone2">
                ¿Buscas algo en concreto?{" "}
                <a href={waDefault()} target="_blank" rel="noopener noreferrer" className="text-wa hover:underline" data-testid="featured-empty-whatsapp">
                  Pregúntanos por WhatsApp
                </a>
              </p>
            </div>
          )}
          {featured && featured.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      {/* MANIFESTO / NOSOTROS */}
      <section id="nosotros" className="border-t border-gold/10 bg-ink2/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-8 md:py-32">
          <Eyebrow>Manifiesto</Eyebrow>
          <h2 className="mt-4 max-w-3xl font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
            La forma <span className="text-gold-metallic">Tecnohogar</span> de trabajar
          </h2>
          <div className="mt-16">
            {CHAPTERS.map((c, i) => (
              <FadeIn key={c.n} delay={0.05 * i}>
                <div className="grid gap-6 border-t border-gold/15 py-10 md:grid-cols-[140px_1fr_2fr] md:gap-10 md:py-14">
                  <span className="font-serif text-5xl text-gold/35 md:text-6xl">{c.n}</span>
                  <h3 className="font-serif text-2xl text-gold md:pt-2 md:text-3xl">{c.title}</h3>
                  <p className="leading-relaxed text-stone2 md:pt-2">{c.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <div className="mt-16 grid items-center gap-12 md:grid-cols-2">
            <FadeIn>
              <div className="relative overflow-hidden rounded-2xl border border-gold/25">
                <img src={ABOUT_IMG} alt="Vivienda representada por Tecnohogar" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/30" />
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <h3 className="font-serif text-2xl text-ivory md:text-4xl">Cómo trabajamos</h3>
              <ul className="mt-8 space-y-6">
                {[
                  { icon: MessagesSquare, t: "Atención directa por WhatsApp", d: `Escribes y te respondemos nosotros, al momento. Nada de espera ni robots: ${PHONE_DISPLAY}.` },
                  { icon: CalendarCheck, t: "Visitas concertadas", d: "Organizamos las visitas cuando te venga bien, incluyendo fines de semana." },
                  { icon: ShieldCheck, t: "Transparencia legal", d: "Documentación revisada y contratos claros antes de cada firma." },
                ].map((f) => (
                  <li key={f.t} className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                      <f.icon size={18} />
                    </span>
                    <div>
                      <p className="font-semibold text-ivory">{f.t}</p>
                      <p className="mt-1 text-sm text-stone2">{f.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CONTACTO */}
      <section id="contacto" className="mx-auto max-w-7xl px-6 py-24 md:px-8 md:py-32">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <Eyebrow>Contacto</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
              Hablemos por <span className="text-gold-metallic">WhatsApp</span>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-stone2">
              Para visitas, dudas o vender tu propiedad: escríbenos y te respondemos al momento. Rellena el formulario y
              se abrirá WhatsApp con tu mensaje listo.
            </p>
            <a
              href={waDefault()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-direct"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 font-bold text-ink transition-transform hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="h-5 w-5" /> {PHONE_DISPLAY}
            </a>
            <div className="mt-8 rounded-xl border border-gold/20 bg-ink2/70 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-gold-subtle">Enlace directo para tus clientes</p>
              <p className="mt-2 text-sm text-stone2">
                Comparte este enlace y tus clientes llegarán directo al formulario de contacto:
              </p>
              <button
                onClick={copyLink}
                data-testid="copy-contact-link-button"
                className="mt-3 flex w-full items-center gap-2 rounded-lg border border-gold/30 px-4 py-2.5 text-left text-sm text-gold transition-colors hover:bg-gold/10 sm:w-fit"
              >
                {copied ? <Check size={15} className="shrink-0" /> : <Copy size={15} className="shrink-0" />}
                <span className="break-all">{copied ? "¡Enlace copiado!" : `${window.location.origin}/#contacto`}</span>
              </button>
            </div>
            <div className="mt-4 rounded-xl border border-gold/20 bg-ink2/70 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-subtle">Ficha «Busca tu piso»</p>
              <p className="mt-2 text-sm text-stone2">
                Envíales este formulario a tus clientes: nos dicen qué buscan, su presupuesto y su situación, y te llega todo por WhatsApp.
              </p>
              <a
                href="/busca-tu-piso"
                data-testid="home-ficha-link"
                className="btn-gold-metallic mt-3 inline-block rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-ink"
              >
                Abrir ficha
              </a>
            </div>
          </div>
          <FadeIn delay={0.1}>
            <div className="rounded-2xl border border-gold/20 bg-ink2/80 p-6 shadow-[0_10px_30px_rgba(0,0,0,0.8)] md:p-8">
              <ContactForm />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="border-t border-gold/10 bg-ink2/40">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center md:py-32">
          <FadeIn>
            <h2 className="font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
              ¿Vas a vender o alquilar <span className="text-gold-metallic">tu propiedad</span>?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-stone2">
              Te hacemos una valoración sin compromiso y nos encargamos de todo: fotos, publicación, visitas y contrato.
            </p>
            <a
              href={waLink("Hola Inmobiliaria Tecnohogar, quiero vender o alquilar mi propiedad. ¿Hablamos?")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-sell-whatsapp"
              className="btn-gold-metallic mt-10 inline-flex items-center gap-3 rounded-full px-10 py-4 text-sm font-bold uppercase tracking-[0.18em] text-ink"
            >
              <WhatsAppIcon className="h-5 w-5" /> Escríbenos por WhatsApp
            </a>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default Home;
